import React from "react";
import ShadowCard from "../../../components/common/ShadowCard";
import { Button } from "@progress/kendo-react-buttons";
import { FaTimes, FaBed, FaPlus } from "react-icons/fa";
import IconComponent from "../../../components/common/IconComponent";
import { Typography } from "@progress/kendo-react-common";
import { FieldArray } from "@progress/kendo-react-form";
import HotelSelectionRow from "./HotelSelectionRow";
import moment from "moment";
import SpecialInclusions from "./SpecialInclusions";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { openDialog, closeDialog } from "../../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../../components/dialog/Dialog";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import "../b2bmodule.scss";

const HotelsList = (props: any) => {
  const {
    value: hotelsValue = [],
    onRemove: onRemoveHotel,
    onInsert: onInsertHotel,
    namePrefix,
    startDate,
    nights,
    destination,
    stayNightOptions,
    onFormChange,
    tripAdults,
    tripChildren,
    tripInfants
  } = props;

  const handleNextNight = (hIdx: number) => {
    const lastHotel = hotelsValue[hIdx];
    const lastNights = lastHotel.Nights || [];
    const lastNightNum = lastNights.length > 0 ? (parseInt(lastNights[lastNights.length - 1]?.label) || 0) : 0;
    const nextNight = lastNightNum + 1;
    const nextNightOption = stayNightOptions.find((opt: any) => opt.label.startsWith(`${nextNight}${nextNight === 1 ? "1st" : nextNight === 2 ? "2nd" : nextNight === 3 ? "3rd" : "th"}`));

    const newHotel = {
      HotelID: 0,
      MealPlanID: 0,
      RoomTypeID: 0,
      Nights: nextNightOption ? [nextNightOption] : [],
      NightPrices: [],
      Comment: "",
      NoOfRooms: lastHotel.NoOfRooms || 1,
      Adults: tripAdults ?? lastHotel.Adults ?? 2,
      AWEB: 0,
      CWEB: tripChildren ?? lastHotel.CWEB ?? 1,
      CNB: 0,
      Infants: tripInfants ?? lastHotel.Infants ?? 0,
      PackageType: lastHotel.PackageType || "PerPerson"
    };
    onInsertHotel({ index: hIdx + 1, value: newHotel });
  };

  const handleDuplicate = (hIdx: number) => {
    onInsertHotel({ index: hIdx + 1, value: JSON.parse(JSON.stringify(hotelsValue[hIdx])) });
  };

  return (
    <>
      {hotelsValue.map((h: any, hIdx: number) => (
        <HotelSelectionRow
          key={hIdx}
          namePrefix={`${namePrefix}.Hotels[${hIdx}]`}
          startDate={startDate}
          nights={nights}
          destination={destination}
          prices={h.NightPrices || []}
          noOfRooms={h.NoOfRooms}
          adults={h.Adults}
          aweb={h.AWEB}
          cweb={h.CWEB}
          cnb={h.CNB}
          infants={h.Infants || 0}
          hotelId={h.HotelID}
          mealId={h.MealPlanID}
          roomId={h.RoomTypeID}
          onFormChange={onFormChange}
          onRemove={() => onRemoveHotel({ index: hIdx })}
          onDuplicate={() => handleDuplicate(hIdx)}
          onNextNight={() => handleNextNight(hIdx)}
          hotelsCount={hotelsValue.length}
          comment={h.Comment}
        />
      ))}
      <div className="mt-3">
        <Button
          type="button"
          fillMode="outline"
          themeColor="primary"
          className="fw-bold px-3 py-1"
          onClick={() => onInsertHotel({ index: hotelsValue.length, value: { HotelID: 0, MealPlanID: 0, RoomTypeID: 0, Nights: [], NightPrices: [], NoOfRooms: 1, Adults: tripAdults || 2, CWEB: tripChildren || 0, Infants: tripInfants || 0, PackageType: "PerPerson" } })}
          style={{ fontSize: "13px", borderRadius: "6px" }}
        >
          + Add Hotel
        </Button>
      </div>
    </>
  );
};

interface PackageOptionSectionProps {
  startDate: any;
  nights: any;
  destination: number | string;
  tripAdults?: number;
  tripChildren?: number;
  tripInfants?: number;
  onFormChange: (name: string, event: any) => void;
  packageTotals?: any[];
}

const PackageOptionItem = ({
  index,
  name,
  hotels: hotelsProp = [],
  namePrefix,
  startDate,
  nights,
  destination,
  stayNightOptions,
  onFormChange,
  tripAdults,
  tripChildren,
  tripInfants,
  packageTotals,
  onRemoveClick,
  showRemoveIcon
}: {
  index: number;
  name: string;
  hotels: any[];
  namePrefix: string;
  startDate: any;
  nights: any;
  destination: number | string;
  stayNightOptions: any[];
  onFormChange: (name: string, event: any) => void;
  tripAdults?: number;
  tripChildren?: number;
  tripInfants?: number;
  packageTotals?: any[];
  onRemoveClick: (idx: number) => void;
  showRemoveIcon?: boolean;
}) => {
  const syncHashRef = React.useRef("");
  const isUpdatingRef = React.useRef(false);

  React.useEffect(() => {
    if (tripAdults === undefined || tripChildren === undefined || tripInfants === undefined || isUpdatingRef.current) return;

    // Use a hash guard to prevent infinite loops from hotelsProp changes
    // Only syncing primary counts: adults, children, infants
    const hash = JSON.stringify({ tripAdults, tripChildren, tripInfants });
    if (syncHashRef.current === hash) return;
    syncHashRef.current = hash;

    let totalChanges = 0;
    (hotelsProp || []).forEach((h, hIdx) => {
      // Selective update: only update if primary counts differ
      const needsUpdate =
        Number(h.Adults) !== Number(tripAdults) ||
        Number(h.CWEB) !== Number(tripChildren) ||
        Number(h.Infants) !== Number(tripInfants);

      if (needsUpdate) {
        if (totalChanges === 0) isUpdatingRef.current = true;
        totalChanges++;

        onFormChange(`${namePrefix}.Hotels[${hIdx}].Adults`, { value: Number(tripAdults) });
        onFormChange(`${namePrefix}.Hotels[${hIdx}].CWEB`, { value: Number(tripChildren) });
        onFormChange(`${namePrefix}.Hotels[${hIdx}].Infants`, { value: Number(tripInfants) });
        // NOTE: AWEB and CNB are preserved as room-level overrides
      }
    });

    if (totalChanges > 0) {
      setTimeout(() => { isUpdatingRef.current = false; }, 200);
    }
  }, [tripAdults, tripChildren, tripInfants, namePrefix, onFormChange, hotelsProp]);

  const accTotal = packageTotals?.[index]?.AccommodationsTotal || 0;

  return (
    <div className="mb-5 last-child-mb-0">
      <div className="option-header">
        <div className="d-flex align-items-center gap-3">
          <div className="option-btn-style d-flex align-items-center gap-2">
            <span>Option {index + 1}: {name}</span>
            <div className="badge bg-primary px-2 py-1" style={{ fontSize: "11px", borderRadius: "10px" }}>
              INR {accTotal.toLocaleString()}
            </div>
            {/* <IconComponent icon={FaChevronDown} style={{ fontSize: "10px" }} /> */}
          </div>
        </div>
        <div className="header-actions">
          {/* <Button type="button" fillMode="outline" className="copy-btn py-1 px-3">
            <IconComponent icon={FaCopy} style={{ fontSize: "11px" }} />
            <span>Copy</span>
          </Button> */}
          {showRemoveIcon && (
            <div
              className="px-2 cursor-pointer opacity-75 hover-opacity-100 transition-all text-white"
              onClick={() => onRemoveClick(index)}
              title="Remove Package Option"
            >
              <IconComponent icon={FaTimes} style={{ fontSize: "14px" }} />
            </div>
          )}
        </div>
      </div>

      <div className="option-content p-4 bg-white rounded-bottom">
        <FieldArray
          name={`${namePrefix}.Hotels`}
          component={HotelsList}
          namePrefix={namePrefix}
          startDate={startDate}
          nights={nights}
          destination={destination}
          stayNightOptions={stayNightOptions}
          onFormChange={onFormChange}
          tripAdults={tripAdults}
          tripChildren={tripChildren}
          tripInfants={tripInfants}
        />

        <SpecialInclusions
          namePrefix={`${namePrefix}.HotelSpecialInclusions`}
          stayNightOptions={stayNightOptions}
          hotelTotals={(hotelsProp || []).map((h: any) => (h.NightPrices || []).reduce((acc: number, p: any) => acc + (Number(p?.GivenPrice) || 0), 0))}
          onFormChange={onFormChange}
          destination={destination}
        />
      </div>
    </div>
  );
};
const PackageOptionsList = (props: any) => {
  const {
    value: optionsValue = [],
    onPush,
    onRemove,
    startDate,
    nights,
    destination,
    tripAdults,
    tripChildren,
    tripInfants,
    onFormChange,
    stayNightOptions,
    packageTotals
  } = props;

  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const [removeIdx, setRemoveIdx] = React.useState<number | null>(null);

  const confirmRemove = () => {
    if (removeIdx !== null) {
      onRemove({ index: removeIdx });
      dispatch(closeDialog());
      setRemoveIdx(null);
    }
  };

  const handleOpenRemoveDialog = (idx: number) => {
    setRemoveIdx(idx);
    dispatch(openDialog("deletePackageOption"));
  };

  return (
    <>
      {optionsValue.map((opt: any, index: number) => {
        return (
          <PackageOptionItem
            key={index}
            index={index}
            name={opt.OptionName || ""}
            hotels={opt.Hotels || []}
            namePrefix={`PackageOptions[${index}]`}
            startDate={startDate}
            nights={nights}
            destination={destination}
            stayNightOptions={stayNightOptions}
            onFormChange={onFormChange}
            tripAdults={tripAdults}
            tripChildren={tripChildren}
            tripInfants={tripInfants}
            packageTotals={packageTotals}
            onRemoveClick={(idx: number) => handleOpenRemoveDialog(idx)}
            showRemoveIcon={optionsValue.length > 1}
          />
        );
      })}

      {dialogName === "deletePackageOption" && removeIdx !== null && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Package Option?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Package Option (<span className="fw-bold">{optionsValue[removeIdx]?.OptionName || `Option ${removeIdx + 1}`}</span>)?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={confirmRemove}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}

      <div className="p-4">
        <Button
          type="button"
          themeColor="primary"
          className="fw-bold px-4 py-2"
          onClick={() => onPush({ value: { OptionName: `Option ${optionsValue.length + 1}`, Hotels: [], HotelSpecialInclusions: [], AccommodationsTotal: 0, TransportsTotal: 0, ActivityTotal: 0, ExtraServicesTotal: 0, OverallTotal: 0, MarkupType: "Amount", MarkupValue: 0, MarkupAmount: 0, GrandTotal: 0 } })}
          style={{ borderRadius: "8px" }}
        >
          <IconComponent icon={FaPlus} className="me-2" /> Add Package Option
        </Button>
      </div>
    </>
  );
};

const PackageOptionSection: React.FC<PackageOptionSectionProps> = ({
  startDate,
  nights,
  destination,
  tripAdults,
  tripChildren,
  tripInfants,
  onFormChange,
  packageTotals
}) => {
  const stayNightOptions = React.useMemo(() => {
    const options = [];
    const totalNights = Number(nights) || 0;
    if (startDate && totalNights > 0) {
      for (let i = 1; i <= totalNights; i++) {
        const date = moment(startDate).add(i - 1, 'days');
        const ordinal = i === 1 ? "1st" : i === 2 ? "2nd" : i === 3 ? "3rd" : `${i}th`;
        const label = `${ordinal} N (${date.format('ddd DD MMM')})`;
        options.push({ label, value: label });
      }
    }
    return options;
  }, [startDate, nights]);

  return (
    <ShadowCard className="b2b-quotation-module b2b-card mb-4 p-0">
      <div className="p-4 d-flex gap-3 align-items-start border-bottom border-light">
        <div className="hotel-icon-wrap shadow-sm">
          <IconComponent icon={FaBed} />
        </div>
        <div className="flex-grow-1">
          <div className="d-flex align-items-center gap-2">
            <Typography.h5 className="section-title mb-0">Hotels</Typography.h5>
          </div>
          <Typography.p className="section-subtitle mb-0 mt-1" style={{ fontSize: "13px" }}>
            Please add hotels details (if included in package) with services provided for each hotels and the selling cost price.
          </Typography.p>
        </div>
      </div>

      <div className="bg-light bg-opacity-50">
        <FieldArray
          name="PackageOptions"
          component={PackageOptionsList}
          startDate={startDate}
          nights={nights}
          destination={destination}
          tripAdults={tripAdults}
          tripChildren={tripChildren}
          tripInfants={tripInfants}
          onFormChange={onFormChange}
          stayNightOptions={stayNightOptions}
          packageTotals={packageTotals}
        />
      </div>
    </ShadowCard>
  );
};

export default PackageOptionSection;
